// @ts-nocheck
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { uniqueId } from '@lmig/lmds-helpers/dist/uniqueId';
import { A11yOnly } from '@lmig/lmds-react-a11y';
import './accordion.scss';
import { AccordionContext } from './context';
import { HeadingAsOptions } from './types';

export interface AccordionProps extends React.ComponentPropsWithoutRef<'div'> {
  /** Add an optional ID to this element: `<div class="lmig-Accordion">`. If none is provided, a unique ID will be generated. */
  id?: string;
  /** Add custom classes to this element: `<div class="lmig-Accordion">`. */
  className?: string;
  /** Set a non-visual label to be used by assistive technologies (e.g., screen readers)
   * for additional audio guidance. */
  labelA11y?: string;
  /** If `true`, only one AccordionTab can be expanded at a time; clicking on another AccordionTab will
   * collapse the previously expanded AccordionTab. When paired with 'collapsible=false",
   * one and only one AccordionTab must be expanded at all times.
  @default false
  */
  singleTab?: boolean;
  /** If `true`, all AccordionTabs can be collapsed at the same time.
   * Otherwise, at least one AccordionTab must be expanded.
  @default true
  */
  collapsible?: boolean;
  /** Set compact styling.
  @default false
  */
  compact?: boolean;
  /** Turn off the top border.
  @default false
  */
  noBorderTop?: boolean;
  /** Use 'list' with shorter expanded content, or when you only have a single accordion on a page.
  @default 'standard'
  */
  variant?: 'standard' | 'list';
  /** Specify the HTML tag to render the visual label of each AccordionTab. Use a tag that conforms to the
   *  hierarchy of the page layout.
   @default 'div'
   */
  as: HeadingAsOptions;
  /** If `true`, the content within an Accordion will take full width.
   * Otherwise, padding will be added on the right side of the Accordion content to align it with the Accordion title.
  @default false
  */
  hasFullWidthContent?: boolean;
  /** Pass only AccordionTab components as children. Children are rendered in this element: `<ul class="lmig-Accordion-list">`. */
  children: ReactNode;
}

/**
 * @see [LMDS Accordion documentation and examples](https://designsystem.lmig.com/components/accordions/code)
 *
 * API:
 *
 * @implements [AccordionProps](https://designsystem.lmig.com/components/accordions/code#accordion-props)
 * @extends [HTMLElement](https://microsoft.github.io/PowerBI-JavaScript/interfaces/_node_modules_typedoc_node_modules_typescript_lib_lib_dom_d_.htmlelement.html)
 */
export const Accordion = ({
  as = 'div',
  children,
  className,
  compact = false,
  hasFullWidthContent = false,
  id: idProp,
  labelA11y,
  noBorderTop = false,
  collapsible = true,
  singleTab = false,
  variant = 'standard',
  ...otherProps
}: AccordionProps): React.ReactElement => {
  const [id] = useState(() => idProp || uniqueId('accordion'));
  const [activeTabIDs, setActiveTabIDs] = useState<Array<string>>([]);
  const accordionRef = useRef(null);

  const labelA11yId = `lmig-accordion-describedby-${id}`;

  // function to handle the toggling of state for each individual accordion tab
  const handleActiveStateTab = ({ elementID, newTabState }: { elementID: string; newTabState: boolean }): void => {
    setActiveTabIDs((prevState) => {
      const included = prevState.includes(elementID);

      let newActiveIDs = prevState.map((prv) => prv);
      if (included) {
        // if included check to remove id from activeTabIDs
        newActiveIDs = singleTab ? [] : newActiveIDs.filter((prv) => prv !== elementID);
      } else if (singleTab && newTabState) {
        // if it's a singleTab and the newTabState coming in is true, we make only that tab active.
        newActiveIDs = [elementID];
      } else if (newTabState) {
        // if the newTabState is true and multiple tabs are allowed, we add to the activeTabIDs array
        newActiveIDs.push(elementID);
      } else {
        // if non of those conditions are true, we filter out the elementID to remove from activeTabIDs
        newActiveIDs = newActiveIDs.filter((actID) => actID !== elementID);
      }
      return newActiveIDs;
    });
  };

  // this effect takes care of the forceFirst that was present before
  useEffect(() => {
    if (!accordionRef || !singleTab || collapsible || activeTabIDs.length > 0) return;
    const { current } = accordionRef;
    const { id: buttonId } = current.querySelector('.lmig-Accordion-list-listItem-header-button');
    handleActiveStateTab({ elementID: buttonId, newTabState: true });
  }, [accordionRef, collapsible, singleTab]);

  return (
    <div
      id={id}
      className={classNames(
        'lmig-Accordion',
        {
          [`lmig-Accordion--${variant}`]: variant !== 'standard',
          'lmig-Accordion--compact': compact,
          'lmig-Accordion--noBorderTop': noBorderTop,
          'lmig-Accordion--hasFullWidthContent': hasFullWidthContent,
        },
        className,
      )}
      aria-describedby={labelA11y ? labelA11yId : null}
      ref={accordionRef}
      {...otherProps}
    >
      <AccordionContext.Provider
        value={{
          accordionId: id,
          compact,
          headingAs: as,
          activeTabIDs,
          collapsible,
          setActiveState: handleActiveStateTab,
        }}
      >
        {labelA11y ? <A11yOnly id={labelA11yId}>{labelA11y}</A11yOnly> : null}
        <ul className={classNames('lmig-Accordion-list')}>{children}</ul>
      </AccordionContext.Provider>
    </div>
  );
};

Accordion.displayName = 'Accordion';
